import { createApi } from '@reduxjs/toolkit/query/react';
import { sortAsc } from '@strata/helper-functions';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PayerSegment {
    segmentId: string;
    payerId: string;
    value: string;
    name: string;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        /** Gets list of payers */
        getPayers: build.query<{ payerId: string; value: string; name: string }[], void>({
            query: () => 'payers/',
            transformResponse: (response: { payer_id: string; payer_name: string }[]) =>
                response
                    ?.map((payer) => ({
                        payerId: payer.payer_id,
                        value: payer.payer_id,
                        name: payer.payer_name,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving payers.'),
        }),
        getSegments: build.query<PayerSegment[], void>({
            query: () => 'payer_segments',
            transformResponse: (
                response: { segment_id: string; payer_id: string; segment_name: string }[],
            ) =>
                response
                    ?.map((segment) => ({
                        segmentId: segment.segment_id,
                        payerId: segment.payer_id,
                        value: segment.segment_id,
                        name: segment.segment_name,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving segment info.',
            ),
        }),
        // Gets list of all Product/Services
        getAllProductServices: build.query<
            {
                productServiceList: { oid: string; name: string; secondaryData: string }[];
                productServiceMap: Record<string, string>;
            },
            void
        >({
            query: () => '/productservices/summaryonly',
            transformResponse: (response: { oid: string; description: string }[]) => {
                const productServiceList = (response ?? [])
                    .map((productService) => ({
                        oid: productService.oid,
                        name: productService.description,
                        secondaryData: productService.oid,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const productServiceMap = Object.fromEntries(
                    productServiceList.map(({ oid, name }) => [oid, name]),
                );
                return { productServiceList, productServiceMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving list of products.',
            ),
        }),
    }),
});

export const { useGetPayersQuery, useGetSegmentsQuery, useGetAllProductServicesQuery } =
    referenceApi;
